import React, { ReactNode, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import IbDebounceInput from '../IbDebounceInput';
import IbIcon from '../IbIcon';
import IbTag from '../IbTag';
import IbButton from '../IbButton';
import IbPopover from '../IbPopover';
import IbTypography from '../IbTypography';
import IbTimepicker from '../IbTimePicker';
import DatePicker from '../DatePicker';
import IbCheckbox from '../IbCheckbox';

import './index.less';

const MAIN_CLASS_NAME = 'filterable-debounce-input';
const SEARCH_INPUT_PREFIX_CLASS_NAME = `${MAIN_CLASS_NAME}__prefix`;
const FILTER_POPOVER_CLASS_NAME = `${MAIN_CLASS_NAME}__filter-popover`;
const FILTER_POPOVER_CONTENT_CLASS_NAME = `${FILTER_POPOVER_CLASS_NAME}__content`;
const FILTER_POPOVER_CONTENT_ITEM_CLASS_NAME = `${FILTER_POPOVER_CONTENT_CLASS_NAME}__item`;
const FILTER_ITEM_DATE_TIME_CLASS_NAME = `${FILTER_POPOVER_CONTENT_ITEM_CLASS_NAME}_date-time`;
const FILTER_ITEM_ACTION_CLASS_NAME = `${FILTER_POPOVER_CONTENT_ITEM_CLASS_NAME}_action`;
const FILTER_DATE_TIME_RANGE_CLASS_NAME = `${FILTER_POPOVER_CONTENT_ITEM_CLASS_NAME}__date-time-range`;
const FILTER_DATE_TIME_RANGE_ITEM_CLASS_NAME = `${FILTER_DATE_TIME_RANGE_CLASS_NAME}__item`;

const EMPTY_VALUE: IFilterableDebounceInputValue = {
  searchText: '',
  filter: undefined,
};

export enum FilterableDebounceInputParam {
  DateTime = 'DateTime',
  Action = 'Action',
}

interface IFilterableDebounceInputFilter {
  startDate?: string;
  endDate?: string;
  startTime?: string;
  endTime?: string;
  withRemain?: boolean;
  withCreate?: boolean;
  withUpdate?: boolean;
  withDelete?: boolean;
}

export interface IFilterableDebounceInputValue {
  searchText?: string;
  filter?: IFilterableDebounceInputFilter;
}

interface IFilterableDebounceInputProps {
  placeholder?: string;
  params?: FilterableDebounceInputParam[];
  value?: IFilterableDebounceInputValue;
  disabled?: boolean;
  onChange?: (value: IFilterableDebounceInputValue) => void;
}

const FilterableDebounceInput: React.FC<IFilterableDebounceInputProps> = ({
  placeholder,
  params,
  value,
  disabled,
  onChange,
}) => {
  if (!params) {
    params = [];
  }

  if (!value) {
    value = EMPTY_VALUE;
  }

  const { t } = useTranslation();

  const [filterPopoverVisible, setFilterPopoverVisible] = useState(false);

  const [startDate, setStartDate] = useState<string>();
  const [endDate, setEndDate] = useState<string>();
  const [startTime, setStartTime] = useState<string>();
  const [endTime, setEndTime] = useState<string>();

  const [withRemain, setWithRemain] = useState<boolean>();
  const [withCreate, setWithCreate] = useState<boolean>();
  const [withUpdate, setWithUpdate] = useState<boolean>();
  const [withDelete, setWithDelete] = useState<boolean>();

  const dependencies = [];

  if (params.includes(FilterableDebounceInputParam.DateTime)) {
    dependencies.push(value.filter?.startDate);
    dependencies.push(value.filter?.startTime);
    dependencies.push(value.filter?.endDate);
    dependencies.push(value.filter?.endTime);
  }

  if (params.includes(FilterableDebounceInputParam.Action)) {
    dependencies.push(value.filter?.withRemain);
    dependencies.push(value.filter?.withCreate);
    dependencies.push(value.filter?.withUpdate);
    dependencies.push(value.filter?.withDelete);
  }

  const filter = useMemo(() => {
    const result = value?.filter || {};
    return Object.values(result).filter((v) => v).length ? result : undefined;
  }, dependencies);

  const onFilterPopoverVisibleChange = (visible?: boolean) => {
    if (visible) return;
    setFilterPopoverVisible(visible || false);
  };

  const onFilterApplyButtonClick = () => {
    const newFilter: IFilterableDebounceInputFilter = {};

    if (params?.includes(FilterableDebounceInputParam.DateTime)) {
      if (startDate) {
        newFilter.startDate = startDate;
        newFilter.startTime = startTime;
      }

      if (endDate) {
        newFilter.endDate = endDate;
        newFilter.endTime = endTime;
      }
    }

    if (params?.includes(FilterableDebounceInputParam.Action)) {
      if (withRemain != null) {
        newFilter.withRemain = withRemain;
      }

      if (withCreate != null) {
        newFilter.withCreate = withCreate;
      }

      if (withUpdate != null) {
        newFilter.withUpdate = withUpdate;
      }

      if (withDelete != null) {
        newFilter.withDelete = withDelete;
      }
    }

    onChange?.({
      searchText: value?.searchText,
      filter: newFilter,
    });
    setFilterPopoverVisible(false);
  };

  const onFilterButtonClick = () => {
    setFilterPopoverVisible(true);
  };

  const onFilterClearButtonClick = () => {
    if (params?.includes(FilterableDebounceInputParam.DateTime)) {
      setStartDate(undefined);
      setEndDate(undefined);
      setStartTime(undefined);
      setEndTime(undefined);
    }

    if (params?.includes(FilterableDebounceInputParam.Action)) {
      setWithRemain(undefined);
      setWithCreate(undefined);
      setWithUpdate(undefined);
      setWithDelete(undefined);
    }

    onChange?.(EMPTY_VALUE);
  };

  const onRemoveDateTimeButtonClick = () => {
    setStartDate(undefined);
    setEndDate(undefined);
    setStartTime(undefined);
    setEndTime(undefined);

    onChange?.({
      ...value,
      filter: {
        ...filter,
        startDate: undefined,
        endDate: undefined,
        startTime: undefined,
        endTime: undefined,
      },
    });
  };

  const onRemoveActionRemainButtonClick = () => {
    setWithRemain(undefined);

    onChange?.({
      ...value,
      filter: {
        ...filter,
        withRemain: undefined,
      },
    });
  };

  const onRemoveActionCreateButtonClick = () => {
    setWithCreate(undefined);

    onChange?.({
      ...value,
      filter: {
        ...filter,
        withCreate: undefined,
      },
    });
  };

  const onRemoveActionUpdateButtonClick = () => {
    setWithUpdate(undefined);

    onChange?.({
      ...value,
      filter: {
        ...filter,
        withUpdate: undefined,
      },
    });
  };

  const onRemoveActionDeleteButtonClick = () => {
    setWithDelete(undefined);

    onChange?.({
      ...value,
      filter: {
        ...filter,
        withDelete: undefined,
      },
    });
  };

  const onDebouncedChange = useCallback(
    (searchText: string) => {
      onChange?.({
        searchText,
        filter,
      });
    },
    [onChange, filter]
  );

  const renderDateTimePopoverContent = () => {
    if (!params?.includes(FilterableDebounceInputParam.DateTime)) {
      return null;
    }

    return (
      <>
        <IbTypography.Paragraph strong>{t('Filterable input: Filter: Search date range')}</IbTypography.Paragraph>
        <div className={`${FILTER_POPOVER_CONTENT_ITEM_CLASS_NAME} ${FILTER_ITEM_DATE_TIME_CLASS_NAME}`}>
          <div className={FILTER_DATE_TIME_RANGE_CLASS_NAME}>
            <IbTypography.Paragraph type="secondary">
              {t('Filterable input: Filter: Search date range: Start')}
            </IbTypography.Paragraph>
            <div className={FILTER_DATE_TIME_RANGE_ITEM_CLASS_NAME}>
              <DatePicker format="DD.MM.YYYY" value={startDate} onChange={setStartDate} />
              <IbTimepicker format="HH:mm" value={startTime} onChange={setStartTime} />
            </div>
          </div>
          <div className={FILTER_DATE_TIME_RANGE_CLASS_NAME}>
            <IbTypography.Paragraph type="secondary">
              {t('Filterable input: Filter: Search date range: End')}
            </IbTypography.Paragraph>
            <div className={FILTER_DATE_TIME_RANGE_ITEM_CLASS_NAME}>
              <DatePicker format="DD.MM.YYYY" value={endDate} onChange={setEndDate} />
              <IbTimepicker format="HH:mm" value={endTime} onChange={setEndTime} />
            </div>
          </div>
        </div>
      </>
    );
  };

  const renderActionPopoverContent = () => {
    if (!params?.includes(FilterableDebounceInputParam.Action)) {
      return null;
    }

    return (
      <>
        <div className={`${FILTER_POPOVER_CONTENT_ITEM_CLASS_NAME} ${FILTER_ITEM_ACTION_CLASS_NAME}`}>
          <IbTypography>
            <IbCheckbox value={withRemain} onChange={setWithRemain} />
            <IbTypography.Paragraph type="secondary">
              {t('Filterable input: Filter: Action: Remain')}
            </IbTypography.Paragraph>
          </IbTypography>
          <IbTypography>
            <IbCheckbox value={withCreate} onChange={setWithCreate} />
            <IbTypography.Paragraph type="secondary">
              {t('Filterable input: Filter: Action: Create')}
            </IbTypography.Paragraph>
          </IbTypography>
          <IbTypography>
            <IbCheckbox value={withUpdate} onChange={setWithUpdate} />
            <IbTypography.Paragraph type="secondary">
              {t('Filterable input: Filter: Action: Update')}
            </IbTypography.Paragraph>
          </IbTypography>
          <IbTypography>
            <IbCheckbox value={withDelete} onChange={setWithDelete} />
            <IbTypography.Paragraph type="secondary">
              {t('Filterable input: Filter: Action: Delete')}
            </IbTypography.Paragraph>
          </IbTypography>
        </div>
      </>
    );
  };

  const renderFilterPopoverContent = () => (
    <div className={FILTER_POPOVER_CONTENT_CLASS_NAME}>
      {renderDateTimePopoverContent()}
      {renderActionPopoverContent()}
      <IbButton type="fill" onClick={onFilterApplyButtonClick}>
        {t('Filterable input: Filter: Apply')}
      </IbButton>
    </div>
  );

  const renderDateTimeTagContent = () => {
    if (!filter) {
      return null;
    }

    return [
      filter.startDate ? t('Filterable input: Filter: Search date range: From') : undefined,
      filter.startDate,
      filter.startTime,
      filter.endDate ? t('Filterable input: Filter: Search date range: To') : undefined,
      filter.endDate,
      filter.endTime,
    ]
      .filter((value) => value)
      .join(' ');
  };

  const renderTags = () => {
    const tags: ReactNode[] = [];

    if (!params || !filter) {
      return tags;
    }

    if (params.includes(FilterableDebounceInputParam.DateTime)) {
      if (filter.startDate || filter.endDate || filter.startTime || filter.endTime) {
        tags.push(<IbTag content={renderDateTimeTagContent()} onDelete={onRemoveDateTimeButtonClick} />);
      }
    }

    if (params.includes(FilterableDebounceInputParam.Action)) {
      if (filter.withRemain) {
        tags.push(
          <IbTag
            content={t('Filterable input: Filter: Action: Remain')}
            style="default"
            onDelete={onRemoveActionRemainButtonClick}
          />
        );
      }
      if (filter.withCreate) {
        tags.push(
          <IbTag
            content={t('Filterable input: Filter: Action: Create')}
            style="success"
            onDelete={onRemoveActionCreateButtonClick}
          />
        );
      }
      if (filter.withUpdate) {
        tags.push(
          <IbTag
            content={t('Filterable input: Filter: Action: Update')}
            style="warning"
            onDelete={onRemoveActionUpdateButtonClick}
          />
        );
      }
      if (filter.withDelete) {
        tags.push(
          <IbTag
            content={t('Filterable input: Filter: Action: Delete')}
            style="error"
            onDelete={onRemoveActionDeleteButtonClick}
          />
        );
      }
    }

    return tags;
  };

  const renderPrefix = () => {
    if (!filter) {
      return <IbIcon iconName="search" />;
    }

    return (
      <div className={SEARCH_INPUT_PREFIX_CLASS_NAME}>
        <IbIcon iconName="search" />
        {renderTags()}
      </div>
    );
  };

  const renderClearSuffix = () => {
    if (!value?.searchText && !filter) {
      return null;
    }

    return <IbButton icon={<IbIcon iconName="close" size={20} />} type="icon" onClick={onFilterClearButtonClick} />;
  };

  const renderFilterSuffix = () => {
    return (
      <IbPopover
        align={{ offset: [-9, 16] }}
        content={renderFilterPopoverContent()}
        placement="bottomLeft"
        trigger="click"
        visible={filterPopoverVisible}
        onVisibleChange={onFilterPopoverVisibleChange}
      >
        <IbButton icon={<IbIcon iconName="filter" size={20} />} type="icon" onClick={onFilterButtonClick} />
      </IbPopover>
    );
  };

  const renderSuffix = () => {
    return (
      <>
        {renderClearSuffix()}
        {renderFilterSuffix()}
      </>
    );
  };

  return (
    <div className={MAIN_CLASS_NAME}>
      <IbDebounceInput
        disabled={disabled}
        placeholder={placeholder}
        prefix={renderPrefix()}
        suffix={renderSuffix()}
        value={value?.searchText}
        onDebouncedChange={onDebouncedChange}
      />
    </div>
  );
};

export default FilterableDebounceInput;
